<template>
  <span>
    <span v-if="!explainable">{{ round(value) }}{{ suffix }}</span>
    <template v-else>
      <span :id="badge_id" class="achievement-badge" @mouseenter="activate">
        {{ round(value) }}{{ suffix }}
        <!-- El info-circle de b-icon en SVG estático: un b-icon por celda pesa en tablas grandes -->
        <svg
          viewBox="0 0 16 16"
          width="1em"
          height="1em"
          fill="currentColor"
          role="img"
          aria-hidden="true"
          class="b-icon bi achievement-badge-icon"
        >
          <g transform="translate(8 8) scale(0.8 0.8) translate(-8 -8)">
            <path
              d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
            />
            <path
              d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"
            />
          </g>
        </svg>
      </span>
      <!-- Se monta al primer paso del mouse: un reporte puede tener miles de celdas -->
      <b-popover
        v-if="is_activated"
        :target="badge_id"
        :show.sync="is_shown"
        triggers="hover"
        placement="left"
        boundary="viewport"
        custom-class="achievement-popover"
        @show="fetchExplanation"
      >
        <template #title>{{
          $t("toolbar.reports.achievementCalculation").toUpperCase()
        }}</template>
        <template>
          <div class="achievement-panel">
            <div v-if="!explanation">{{ $t("reusable.common.loading") }}</div>
            <div v-else-if="explanation.error" class="achievement-issue">
              {{ explanation.error }}
            </div>
            <template v-else>
              <div
                class="achievement-verdict"
                :style="{ backgroundColor: explanation.achievement_color }"
              >
                {{ categoryLabel() }}: {{ explanation.achievement }}%
              </div>
              <div class="achievement-reason">{{ explanation.reason }}</div>
              <div v-if="explanation.chips.length" class="achievement-chips">
                <span
                  v-for="chip in explanation.chips"
                  :key="chip.label"
                  class="achievement-chip"
                >
                  {{ chip.label }}: <strong>{{ chip.value }}</strong>
                </span>
              </div>
              <div
                v-for="(issue, issue_index) in explanation.issues"
                :key="`issue-${issue_index}`"
                class="achievement-issue"
              >
                {{ issue }}
              </div>
              <div
                v-for="(group, group_index) in explanation.groups"
                :key="`group-${group_index}`"
                class="achievement-group"
                :class="group.tag ? `group-${group.tag}` : ''"
              >
                <div v-if="group.title" class="achievement-group-title">
                  <span
                    class="achievement-grade"
                    :class="`grade-${group.grade_state}`"
                    :title="gradeStateLabel(group.grade_state)"
                    >{{ group.grade }}</span
                  >
                  <span class="achievement-group-name">{{ group.title }}</span>
                  <span class="achievement-period">{{ group.period }}</span>
                  <span
                    v-if="group.tag"
                    class="achievement-tag"
                    :class="`tag-${group.tag}`"
                    >{{ tagLabel(group.tag) }}</span
                  >
                  <span
                    class="achievement-value"
                    :style="{ backgroundColor: group.achievement_color }"
                    >{{ group.achievement }}%</span
                  >
                </div>
                <div
                  v-for="(row, row_index) in group.rows"
                  :key="`row-${group_index}-${row_index}`"
                >
                  <div
                    class="achievement-row"
                    :class="{ 'row-not-counted': !row.counts }"
                  >
                    <span
                      class="achievement-value"
                      :style="{ backgroundColor: row.achievement_color }"
                      >{{
                        row.achievement != null ? row.achievement : "-"
                      }}</span
                    >
                    <span
                      class="achievement-row-name"
                      :title="
                        row.hint ? `${row.hint}: ${row.label}` : row.label
                      "
                      >{{ row.label }}</span
                    >
                    <span class="achievement-share">
                      <!-- Un tributario no cuenta si el alumno no tiene logro en él o si pesa 0 -->
                      <template v-if="!row.counts">{{
                        row.achievement == null
                          ? $t("toolbar.reports.notConsideredNoAchievement")
                          : $t("toolbar.reports.notConsideredNoWeight")
                      }}</template>
                      <template v-else>
                        <template v-if="row.weight_label"
                          >{{ row.weight_label }} ·
                        </template>
                        {{ $t("toolbar.reports.contributes") }}
                        {{ round(row.share, 0) }}%
                      </template>
                    </span>
                  </div>
                  <!-- Instrumento: cuánto vale y cómo aportan sus preguntas o dimensiones -->
                  <div v-if="row.items" class="achievement-items">
                    <div class="achievement-row-detail">{{ row.detail }}</div>
                    <div
                      v-for="(item, item_index) in row.items"
                      :key="`item-${group_index}-${row_index}-${item_index}`"
                      class="achievement-row achievement-item"
                    >
                      <span
                        class="achievement-value achievement-item-value"
                        :style="{ backgroundColor: item.achievement_color }"
                        >{{ item.achievement }}</span
                      >
                      <span
                        class="achievement-row-name"
                        :title="itemLabel(item)"
                        >{{ itemLabel(item) }}</span
                      >
                      <span class="achievement-share">
                        {{ itemWeightLabel(item) }} ·
                        {{ $t("toolbar.reports.contributes") }}
                        {{ round(item.share, 0) }}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </template>
      </b-popover>
    </template>
  </span>
</template>

<script>
// Contador de módulo: id de DOM único por instancia, aunque el mismo logro se
// renderice más de una vez (mismo criterio que RegimeExplanationBadge)
let achievement_badge_instance_counter = 0;
</script>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
import { computed, getCurrentInstance, ref } from "vue";

const props = defineProps({
  evaluatee_id: {
    type: [Number, String],
    required: true,
  },
  assessment_id: {
    type: [Number, String],
    default: null,
  },
  section_id: {
    type: [Number, String],
    default: null,
  },
  // summative | formative
  category: {
    type: String,
    required: true,
  },
  value: {
    default: null,
  },
  achievement_range_group: {
    default: null,
  },
  // Unidad que el reporte muestra junto al valor
  suffix: {
    type: String,
    default: "%",
  },
  // Sin desglose (DUOC) se muestra solo el valor
  explainable: {
    type: Boolean,
    default: true,
  },
});

const { proxy } = getCurrentInstance();
// Una explicación por valor (sumativo o formativo), pedida la primera vez que se abre
const explanations = ref({});
const explanation = computed(() => explanations.value[props.category]);
const is_activated = ref(false);
const is_shown = ref(false);

achievement_badge_instance_counter += 1;
const instance_id = achievement_badge_instance_counter;
const badge_id = computed(
  () => `achievement-badge-${instance_id}-${props.assessment_id}`
);

// Mismo redondeo que el filtro global Round
function round(value, decimals = 2) {
  const number = parseFloat(value);
  if (isNaN(number)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round((number + Number.EPSILON) * factor) / factor;
}

function categoryLabel() {
  if (props.category === "summative")
    return proxy.$t("toolbar.reports.summativeAchievement");
  return proxy.$t("toolbar.reports.formativeAchievement");
}

function tagLabel(tag) {
  if (tag === "selected") return proxy.$t("toolbar.reports.sectionSelected");
  return proxy.$t("toolbar.reports.sectionDiscarded");
}

// Pregunta o dimensión de rúbrica, con su orden en el instrumento
function itemLabel(item) {
  const name =
    item.kind === "rubric_criteria"
      ? proxy.$getVisibleNames("manual.dimension", false, "Dimensión")
      : proxy.$t("toolbar.reports.itemQuestion");
  return item.text
    ? `${name} ${item.order}: ${item.text}`
    : `${name} ${item.order}`;
}

// La pregunta pondera por su puntaje máximo; la dimensión, por su ponderación en la pauta
function itemWeightLabel(item) {
  if (item.kind === "rubric_criteria") return `${round(item.weight, 1)}%`;
  return `${round(item.weight, 2)} ${proxy.$t("toolbar.reports.points")}`;
}

function gradeStateLabel(state) {
  if (state === "approved") return proxy.$t("toolbar.reports.gradeApproved");
  if (state === "failed") return proxy.$t("toolbar.reports.gradeFailed");
  return proxy.$t("toolbar.reports.gradeInProgress");
}

// Primer paso del mouse: monta el popover ya abierto; desde ahí lo maneja su propio hover
function activate() {
  if (is_activated.value) return;
  is_activated.value = true;
  is_shown.value = true;
  fetchExplanation();
}

// Valores con la petición en curso, para no repetirla si el panel se abre de nuevo antes de la respuesta
const pending_categories = new Set();

function fetchExplanation() {
  const category = props.category;
  if (explanations.value[category] || pending_categories.has(category)) return;
  pending_categories.add(category);
  proxy.$restful
    .Post(`/assessment2/evaluatee_assessment_explanation/`, {
      evaluatee: props.evaluatee_id,
      assessment: props.assessment_id,
      section: props.section_id,
      category: category,
      achievement_range_group: props.achievement_range_group,
    })
    .then((response) => {
      explanations.value = { ...explanations.value, [category]: response };
    })
    .catch(() => {
      explanations.value = {
        ...explanations.value,
        [category]: { error: proxy.$t("reusable.common.problemGenerating") },
      };
    })
    .finally(() => pending_categories.delete(category));
}
</script>

<style scoped>
/* Valor en la celda: el ícono anticipa el desglose */
.achievement-badge {
  white-space: nowrap;
  cursor: help;
  border-bottom: 1px dotted rgba(0, 0, 0, 0.45);
}
.achievement-badge-icon {
  opacity: 0.55;
}
.achievement-badge:hover .achievement-badge-icon {
  opacity: 0.9;
}
.achievement-panel {
  max-height: 440px;
  overflow: auto;
  min-width: 340px;
  font-size: 9.5pt;
  text-align: left;
}
.achievement-verdict {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-weight: bold;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
.achievement-reason {
  margin: 4px 0;
}
.achievement-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}
.achievement-chip {
  background-color: #eceff1;
  border-radius: 8px;
  padding: 1px 6px;
  font-size: 8pt;
}
.achievement-issue {
  background-color: #fdecea;
  border-left: 3px solid #c62828;
  padding: 2px 6px;
  margin-bottom: 3px;
}
.achievement-group {
  border-left: 3px solid #cfd8dc;
  padding-left: 6px;
  margin-top: 5px;
}
.achievement-group.group-selected {
  border-left-color: var(--kl-color-accent-medium, #1565c0);
}
.achievement-group.group-discarded {
  border-left-color: #e0e0e0;
  opacity: 0.75;
}
.achievement-group-title {
  display: flex;
  align-items: baseline;
  gap: 5px;
  font-weight: bold;
  margin-bottom: 2px;
}
.achievement-group-name {
  flex: 1;
}
.achievement-period {
  color: #78909c;
  font-size: 8pt;
  font-weight: normal;
}
.achievement-tag {
  font-weight: normal;
  font-size: 7.5pt;
  border-radius: 8px;
  padding: 0 5px;
  color: #fff;
}
.tag-selected {
  background-color: var(--kl-color-accent-medium, #1565c0);
}
.tag-discarded {
  background-color: #bdbdbd;
}
.achievement-grade {
  min-width: 26px;
  text-align: center;
  border-radius: 4px;
  font-weight: bold;
  color: #fff;
}
.grade-approved {
  background-color: #2e7d32;
}
.grade-failed {
  background-color: #c62828;
}
.grade-in_progress {
  background-color: #ef6c00;
}
.achievement-row {
  display: flex;
  align-items: baseline;
  gap: 5px;
}
.achievement-row.row-not-counted {
  color: #9e9e9e;
}
.achievement-value {
  min-width: 44px;
  text-align: center;
  border-radius: 4px;
  font-weight: bold;
  font-size: 8.5pt;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.achievement-row-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.achievement-share {
  color: #78909c;
  font-size: 8pt;
  white-space: nowrap;
}
.achievement-items {
  margin: 1px 0 4px 22px;
  padding-left: 6px;
  border-left: 1px dashed #cfd8dc;
}
.achievement-row-detail {
  color: #78909c;
  font-size: 8pt;
}
.achievement-item {
  font-size: 8.5pt;
}
.achievement-item-value {
  min-width: 36px;
  font-size: 8pt;
}
</style>

<!-- Sin scoped: BootstrapVue monta el popover fuera del componente, así que el
     contenedor .popover no recibe el atributo de scope. La clase propia acota el
     alcance para no afectar al resto de los popovers. -->
<style>
.popover.achievement-popover {
  max-width: 440px;
}
.popover.achievement-popover .popover-body {
  padding: 8px 10px;
}
</style>
